/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { ChangeEvent, } from "react";
import { styled } from "@mui/material";
import { CaretDownIcon, CaretUpIcon } from "../../FontAwesome";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import StringUtil from "../../Utils/StringUtil";
import TextInput from "../TextInput";
import GanzzahlFormat from "./GanzzahlFormat";
import GanzzahlInputProps from "./GanzzahlInputProps";
import "./GanzzahlInput.scss";

const IconContainer = styled("span")({
    display: "inline-flex",
    flexDirection: "column",
    marginInlineEnd: "-4px",
    "& > button": {
        "&:first-child": {
            marginBottom: "-2px",
        },
        "&:last-child": {
            marginTop: "-2px",
        }
    },
});

const IconButton = styled("button")({
    border: "none",
    outline: "none",
    background: "transparent",
    margin: 0,
    padding: 0,
    color: "rgb(0 0 0 / .54)",
    cursor: "pointer",
    "&:disabled": {
        color: "rgb(0 0 0 / .26)",
    },
});

export default function GanzzahlInput(props: GanzzahlInputProps) {
    const { t } = useTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    function bereinige(wert: number | string) {
        if (typeof wert === "string" && StringUtil.isEmpty(wert)) {
            return null;
        }

        const zahl = Number(wert);
        if (zahl < props.min || (props.max !== undefined && zahl > props.max)) {
            return props.value;
        }

        return zahl;
    }

    function verlassen(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const neuerWert = e.target.value;
        props.onBlur?.(bereinige(neuerWert));
    }

    function aendern(e: ChangeEvent<HTMLInputElement |HTMLTextAreaElement>) {
        const neuerWert = e.target.value;
        props.onChange(bereinige(neuerWert));
    }

    function erhoehen() {
        inputRef.current?.focus();
        const neueGanzzahl = props.value !== null
            ? props.value + 1
            : 1;
        props.onChange(bereinige(neueGanzzahl))
    }

    function vermindern() {
        inputRef.current?.focus();
        const neueGanzzahl = props.value !== null
            ? props.value - 1
            : 1;
        props.onChange(bereinige(neueGanzzahl));
    }

    return (
        <TextInput
            {...props}
            value={props.value?.toString()}
            onBlur={verlassen}
            onChange={aendern}
            inputRef={inputRef}
            InputProps={{
                inputComponent: GanzzahlFormat as any,
                inputProps: {
                    min: props.min,
                    max: props.max,
                    value: props.value
                },
                endAdornment: (
                    <IconContainer>
                        <IconButton
                            title={t(translationKeys.ganzzahlInput.auswahlErhoehenTitle)}
                            onClick={erhoehen}
                            disabled={props.readonly}
                            tabIndex={-1}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <CaretUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            title={t(translationKeys.ganzzahlInput.auswahlVerringernTitle)}
                            onClick={vermindern}
                            disabled={props.readonly}
                            tabIndex={-1}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <CaretDownIcon fontSize="small" />
                        </IconButton>
                    </IconContainer>
                ),
            }}
        />
    );
}
